<template>
  <div class="tin-root">
    <div class="ambient"></div>
    <div class="ambient-floor"></div>
    <div class="grain"></div>
    <div class="frame"></div>
    <div class="frame-inner"></div>

    <div class="page">

      <!-- ── BACK ── -->
      <button class="back-btn" @click="goBack">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="15 18 9 12 15 6"/></svg>
        <span>Back to {{ cityName || 'Chapter' }}</span>
      </button>

      <!-- ── SECTION HEADER ── -->
      <div class="section-hd">
        <div class="section-rule l"></div>
        <span class="section-eyebrow">Talk of the Town</span>
        <div class="section-rule"></div>
      </div>
      <h2 class="section-title">New Post <em v-if="cityName">in {{ cityName }}</em></h2>
      <p class="section-lede">Start a conversation with the rest of this chapter — a question, a heads up, a plan for who's around.</p>

      <!-- ── FORM ── -->
      <div class="form-wrap">
        <div class="field">
          <label class="field-label">Your Post</label>
          <textarea
            v-model="postText"
            class="field-textarea"
            rows="5"
            placeholder="What's on your mind for this chapter?"
          ></textarea>
          <div class="field-line"></div>
        </div>

        <div class="field">
          <label class="field-label">Add Photos or Videos (Optional)</label>

          <div v-if="mediaPreviews.length" class="media-grid">
            <div v-for="(item, idx) in mediaPreviews" :key="idx" class="media-thumb">
              <img v-if="item.type === 'image'" :src="item.url" />
              <video v-else :src="item.url" controls></video>
              <button class="media-remove-btn" @click="removeMediaFile(idx)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          </div>

          <div class="img-upload-area" @click="openFilePicker">
            <div class="img-upload-placeholder">
              <div class="upload-seal">
                <div class="seal-ring"></div>
                <div class="seal-diamond"></div>
              </div>
              <p class="upload-hint">{{ mediaPreviews.length ? 'Add more' : 'Click to select photos or videos' }}</p>
            </div>
          </div>
          <input
            type="file"
            ref="fileInput"
            style="display: none;"
            accept="image/*,video/*"
            multiple
            @change="handleFileChange"
          />
        </div>

        <div class="form-actions">
          <button class="submit-btn" :disabled="!postText.trim() || isSubmitting" @click="submitPost">
            <span v-if="isSubmitting" class="btn-loading">
              <div class="btn-seal"><div class="seal-ring"></div></div>
              Posting…
            </span>
            <span v-else class="btn-inner">
              <div class="btn-tick"></div>
              Post to Chapter
              <div class="btn-tick"></div>
            </span>
          </button>
          <button class="cancel-btn" @click="goBack">Cancel</button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { doc, getDoc, addDoc, collection, serverTimestamp } from "firebase/firestore";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, auth } from "@/firebase";

export default {
  name: "CreatePostView",
  props: {
    id: String, // the chapter's groups-collection doc id, from /group/:id/create-post
  },

  data() {
    return {
      cityName: "",
      postText: "",
      mediaFiles: [],
      mediaPreviews: [], // [{ url, type: 'image'|'video' }]
      isSubmitting: false,
    };
  },

  async mounted() {
    try {
      const snap = await getDoc(doc(db, "groups", this.id));
      if (snap.exists()) this.cityName = snap.data().city || "";
    } catch (err) {
      console.error("Failed to load chapter for post creation:", err);
    }
  },

  methods: {
    openFilePicker() {
      this.$refs.fileInput.click();
    },

    handleFileChange(event) {
      const files = Array.from(event.target.files || []);
      files.forEach((file) => {
        this.mediaFiles.push(file);
        this.mediaPreviews.push({
          url: URL.createObjectURL(file),
          type: file.type.startsWith("image") ? "image" : "video",
        });
      });
      event.target.value = "";
    },

    removeMediaFile(idx) {
      this.mediaFiles.splice(idx, 1);
      this.mediaPreviews.splice(idx, 1);
    },

    goBack() {
      if (this.cityName) this.$router.push(`/groups/${encodeURIComponent(this.cityName)}`);
      else this.$router.push("/groups");
    },

    async uploadAllMedia() {
      const storage = getStorage();
      return Promise.all(
        this.mediaFiles.map(async (file, idx) => {
          const isImage = file.type.startsWith("image");
          const ext = isImage ? "jpg" : "mp4";
          const fileRef = storageRef(storage, `chapter_posts/${this.id}_${idx}_${Date.now()}.${ext}`);
          await uploadBytes(fileRef, file);
          const url = await getDownloadURL(fileRef);
          return { url, type: isImage ? "image" : "video" };
        })
      );
    },

    async submitPost() {
      if (!this.postText.trim() || this.isSubmitting) return;
      const user = auth.currentUser;
      if (!user) return;

      this.isSubmitting = true;
      try {
        let fullName = user.displayName || "Anonymous";
        let profileImage = user.photoURL || "";
        const userSnap = await getDoc(doc(db, "user", user.uid));
        if (userSnap.exists()) {
          const userData = userSnap.data();
          fullName = userData.fullName || fullName;
          profileImage = userData.image || profileImage;
        }

        const media = this.mediaFiles.length ? await this.uploadAllMedia() : [];

        await addDoc(collection(db, "posts"), {
          groupID: this.id,
          uid: user.uid,
          userName: fullName,
          userProfileImageURL: profileImage,
          text: this.postText.trim(),
          media,
          likes: [],
          commentsCount: 0,
          timestamp: serverTimestamp(),
        });

        this.goBack();
      } catch (err) {
        console.error("Failed to create chapter post:", err);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

.tin-root { position: relative; min-height: 100vh; width: 100%; background: #070708; font-family: 'Montserrat', sans-serif; }

.ambient { position: fixed; top: -300px; left: 50%; transform: translateX(-50%); width: 1000px; height: 800px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 10%, rgba(210,175,100,0.09) 0%, rgba(210,175,100,0.03) 30%, transparent 65%); }
.ambient-floor { position: fixed; bottom: -200px; left: 50%; transform: translateX(-50%); width: 700px; height: 400px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 100%, rgba(210,175,100,0.03) 0%, transparent 60%); }
.grain { position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: 0.5; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.12'/%3E%3C/svg%3E"); background-size: 180px 180px; }
.frame { position: fixed; inset: 20px; border: 1px solid rgba(210,175,100,0.07); z-index: 2; pointer-events: none; }
.frame::before, .frame::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.frame::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner { position: fixed; inset: 20px; z-index: 2; pointer-events: none; }
.frame-inner::before, .frame-inner::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame-inner::before { top: -1px; right: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner::after  { bottom: -1px; left: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }

.page { position: relative; z-index: 10; max-width: 680px; margin: 0 auto; padding: 48px 24px 100px; animation: fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

/* ── BACK ── */
.back-btn { display: flex; align-items: center; gap: 8px; background: none; border: none; color: rgba(210,175,100,0.45); font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; cursor: pointer; margin-bottom: 40px; padding: 0; transition: color 0.2s; }
.back-btn:hover { color: rgba(210,175,100,0.8); }
.back-btn svg { width: 16px; height: 16px; }

/* ── SECTION HEADER ── */
.section-hd { display: flex; align-items: center; gap: 20px; margin-bottom: 20px; }
.section-rule { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(210,175,100,0.18), transparent); }
.section-rule.l { background: linear-gradient(90deg, transparent, rgba(210,175,100,0.18)); }
.section-eyebrow { font-size: 9px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.45); text-transform: uppercase; white-space: nowrap; }
.section-title { font-family: 'Cormorant', serif; font-size: 38px; font-weight: 300; color: #edeae2; letter-spacing: -0.5px; margin-bottom: 14px; line-height: 1.1; }
.section-title em { font-style: italic; color: rgba(210,175,100,0.8); font-weight: 300; }
.section-lede { font-size: 13px; font-weight: 300; line-height: 1.75; color: rgba(237,232,223,0.4); margin-bottom: 44px; }

/* ── FORM ── */
.form-wrap { display: flex; flex-direction: column; gap: 36px; }
.field { display: flex; flex-direction: column; gap: 10px; position: relative; }
.field-label { font-size: 9px; font-weight: 600; letter-spacing: 3.5px; text-transform: uppercase; color: rgba(210,175,100,0.45); }
.field-textarea { background: transparent; border: none; border-bottom: 1px solid rgba(237,232,223,0.12); padding: 12px 0; color: #edeae2; font-family: 'Montserrat', sans-serif; font-size: 15px; font-weight: 300; outline: none; caret-color: rgba(210,175,100,0.8); transition: border-color 0.3s; letter-spacing: 0.3px; resize: vertical; }
.field-textarea::placeholder { color: rgba(237,232,223,0.15); font-style: italic; }
.field-textarea:focus { border-bottom-color: rgba(210,175,100,0.4); }
.field-line { position: absolute; bottom: 0; left: 0; height: 1px; width: 0; background: linear-gradient(90deg, rgba(210,175,100,0.8), rgba(210,175,100,0.2)); transition: width 0.5s cubic-bezier(0.25,0.46,0.45,0.94); pointer-events: none; }
.field-textarea:focus ~ .field-line { width: 100%; }

/* ── IMAGE UPLOAD ── */
.img-upload-area { border: 1px solid rgba(210,175,100,0.12); cursor: pointer; overflow: hidden; transition: border-color 0.3s; }
.img-upload-area:hover { border-color: rgba(210,175,100,0.3); }
.img-preview { width: 100%; max-height: 320px; object-fit: cover; display: block; filter: brightness(0.85) saturate(0.75); }
.media-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 12px; }
.media-thumb { position: relative; aspect-ratio: 1; border: 1px solid rgba(210,175,100,0.2); overflow: hidden; }
.media-thumb img, .media-thumb video { width: 100%; height: 100%; object-fit: cover; display: block; }
.media-remove-btn { position: absolute; top: 4px; right: 4px; width: 22px; height: 22px; border-radius: 50%; background: rgba(0,0,0,0.7); border: 1px solid rgba(210,175,100,0.3); color: rgba(237,232,223,0.8); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background 0.2s, color 0.2s; }
.media-remove-btn:hover { background: rgba(200,60,60,0.7); color: #fff; }
.media-remove-btn svg { width: 12px; height: 12px; }
.img-upload-placeholder { width: 100%; height: 140px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; }
.upload-seal { position: relative; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; }
.seal-ring { position: absolute; inset: 0; border-radius: 50%; border: 1px solid rgba(210,175,100,0.2); }
.seal-diamond { width: 9px; height: 9px; border: 1px solid rgba(210,175,100,0.6); transform: rotate(45deg); }
.upload-hint { font-size: 10px; font-weight: 500; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(237,232,223,0.2); }

/* ── ACTIONS ── */
.form-actions { display: flex; flex-direction: column; gap: 12px; padding-top: 8px; }
.submit-btn { width: 100%; border: 1px solid rgba(210,175,100,0.3); background: transparent; padding: 18px; cursor: pointer; font-family: 'Montserrat', sans-serif; transition: border-color 0.3s, background 0.3s, opacity 0.2s; }
.submit-btn:not(:disabled):hover { border-color: rgba(210,175,100,0.6); background: rgba(210,175,100,0.06); }
.submit-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.btn-inner { display: flex; align-items: center; justify-content: center; gap: 14px; font-size: 10px; font-weight: 600; letter-spacing: 4px; text-transform: uppercase; color: rgba(210,175,100,0.8); }
.btn-tick { width: 20px; height: 1px; background: rgba(210,175,100,0.4); }
.btn-loading { display: flex; align-items: center; justify-content: center; gap: 12px; font-size: 10px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; color: rgba(210,175,100,0.5); }
.btn-seal { position: relative; width: 18px; height: 18px; }
.cancel-btn { width: 100%; border: none; background: transparent; padding: 14px; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 3.5px; text-transform: uppercase; color: rgba(237,232,223,0.2); transition: color 0.2s; }
.cancel-btn:hover { color: rgba(237,232,223,0.45); }

@media (max-width: 600px) { .page { padding: 32px 20px 80px; } .frame, .frame-inner { inset: 12px; } .section-title { font-size: 30px; } }
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
</style>